// SPDX-FileCopyrightText: 2023 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { get } from '@tls/auth';
import { AxiosRequestConfig } from 'axios';
import { useCallback, useState } from 'react';
import saveFile from 'utilities/saveFile';

const options: AxiosRequestConfig = {
  responseType: 'blob',
};

export type DownloadFileOptions = {
  signal?: AbortSignal;
  onTransferProgress?: (progress?: number) => void;
};

function getTitle(headers) {
  return headers['content-disposition']
    ?.split(';')
    ?.find((c: string) => c.trim().startsWith('filename='))
    ?.replace('filename=', '')
    ?.replaceAll('"', '')
    ?.trim();
}

export default function useDownloadFileApi() {
  const [loading, setLoading] = useState(false);
  const [lastStatus, setLastStatus] = useState(200);

  const downloadAndSaveFile = useCallback(
    async (url: string, defaultFileName: string, downloadOptions: DownloadFileOptions = {}) => {
      setLoading(true);
      setLastStatus(200);

      try {
        const requestOptions: AxiosRequestConfig = {
          ...options,
          signal: downloadOptions.signal,
          ...(downloadOptions.onTransferProgress && {
            onDownloadProgress: progressEvent => downloadOptions.onTransferProgress?.(progressEvent.progress),
          }),
        };
        const result = await get(url, false, false, requestOptions);
        const { data, status, headers } = result;
        setLastStatus(status);

        if (status === 200) {
          const title = getTitle(headers) ?? defaultFileName;
          saveFile(data, title);
        }
        return status;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { loading, lastStatus, downloadAndSaveFile };
}
